<template>
  <AlertMessage
    :message="alertMessage"
    :alertType="alertType ? alertType : 'empty'"
    @close="alertClose"
  />

  <UsersPreviewFromSpace
    :open="openUsersList"
    @update:open="updateOpen"
    :withoutRemove="true"
    :spaceId="spaceId"
    :spaceNameId="spaceNameId"
  />

  <DataTable
    :data="polls"
    :columns="columns"
    :loading="loading"
    :sort-field="sortField"
    :sort-direction="sortDirection"
    :current-page="currentPage"
    :total-pages="totalPages"
    :total-items="totalItems"
    :page-size="pageSize"
    @sort="handleSort"
    @page-change="goToPage"
    @page-size-change="handlePageSizeChange"
    @prev-page="prevPage"
    @next-page="nextPage"
  >
    <template #filters v-if="showFilters">
      <TableFilters
        :checkboxes="filterCheckboxes"
        :date-range="{ start: showPollStartTime, end: showPollEndTime }"
        @checkbox-change="handleCheckboxChange"
        @date-change="handleDateChange"
        @clear-filters="clearFilters"
      />
    </template>

    <template #cell-name="{ item }">
      <span class="text-sm font-semibold text-gray-900">
        {{ item.name || "Untitled poll" }}
      </span>
    </template>

    <template #cell-isAnonymous="{ item }">
      <div class="flex justify-start">
        <span
          :class="[
            'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide',
            item.isAnonymous
              ? 'border-emerald-100 bg-emerald-50 text-emerald-600'
              : 'border-gray-200 bg-gray-100 text-gray-500',
          ]"
        >
          {{ item.isAnonymous ? "Yes" : "No" }}
        </span>
      </div>
    </template>

    <template #cell-spaceId="{ item }">
      <span
        class="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600"
      >
        {{ item.spaceId ? "Groups" : "Individual" }}
      </span>
    </template>

    <template #cell-status="{ item }">
      <span
        :class="[
          'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide whitespace-nowrap',
          getStatusClasses(item.status),
        ]"
      >
        {{ formatStatus(item.status) }}
      </span>
    </template>

    <template #cell-actions="{ item }">
      <div class="flex items-center justify-start gap-2">
        <TableActionButton icon="view" title="View" @click="viewPoll(item)" />
        <TableActionButton icon="delete" title="Delete" variant="danger" />
      </div>
    </template>
  </DataTable>
</template>

<script>
import UsersPreviewFromSpace from "@/components/UsersPreviewFromSpace.vue";
import { getAllQuestionnaires } from "@/services/questionnaire";
import AlertMessage from "@/components/AlertMessage.vue";
import DataTable from "@/components/ui/DataTable.vue";
import TableFilters from "@/components/ui/TableFilters.vue";
import TableActionButton from "@/components/ui/TableActionButton.vue";
import { DateTime } from "luxon";

export default {
  emits: ["update-total"],
  props: {
    showFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      polls: [],
      spaceId: null,
      spaceNameId: "",
      openUsersList: false,
      alertMessage: "",
      alertType: "",
      currentPage: 1,
      pageSize: 6,
      showActivePolls: true,
      showFinisedPolls: false,
      pollStatus: null,
      totalItems: 0,
      showPollStartTime: "",
      showPollEndTime: "",
      pollDateStart: null,
      pollDateEnd: null,
      loading: false,
      sortField: null,
      sortDirection: null,
      sortFieldMapping: {
        name: "name",
        isAnonymous: "is_anonymous",
        createdAt: "created_at",
        spaceId: "space_id",
        status: "status",
      },
    };
  },
  components: {
    UsersPreviewFromSpace,
    AlertMessage,
    DataTable,
    TableFilters,
    TableActionButton,
  },
  mounted() {
    this.getPolls();
  },
  watch: {
    showActivePolls() {
      this.getPolls();
    },
    showFinisedPolls() {
      this.pollStatus = this.showFinisedPolls ? "COMPLETED" : null;
      this.getPolls();
    },
    pollDateStart(newDate) {
      this.getPolls(newDate, this.pollDateEnd);
    },
    pollDateEnd(newDate) {
      this.getPolls(this.pollDateStart, newDate);
    },
  },
  computed: {
    columns() {
      return [
        {
          key: "name",
          label: "Name",
          sortable: true,
          width: "30%",
          cellClass: "align-top text-sm font-semibold text-gray-900",
          skeletonClass: "w-40",
          formatter: (value) => value || "Untitled poll",
        },
        {
          key: "isAnonymous",
          label: "Anonymous",
          sortable: true,
          width: "140px",
          headerClass: "hidden md:table-cell",
          cellClass: "hidden md:table-cell",
          skeletonClass: "w-20",
        },
        {
          key: "createdAt",
          label: "Created",
          sortable: true,
          width: "160px",
          headerClass: "hidden md:table-cell",
          cellClass: "hidden md:table-cell text-sm text-gray-500",
          skeletonClass: "w-28",
          formatter: (value) => this.formatDate(value) || "01 March, 2022",
        },
        {
          key: "spaceId",
          label: "Type",
          sortable: true,
          width: "150px",
          headerClass: "hidden md:table-cell",
          cellClass: "hidden md:table-cell",
          skeletonClass: "w-24",
        },
        {
          key: "status",
          label: "Status",
          sortable: true,
          width: "150px",
          headerClass: "hidden md:table-cell",
          cellClass: "hidden md:table-cell",
          skeletonClass: "w-24",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          width: "120px",
          headerClass: "text-left",
          cellClass: "align-top text-left",
          skeletonClass: "w-16",
        },
      ];
    },
    filterCheckboxes() {
      return [
        { key: "active", label: "Only Active", value: this.showActivePolls },
        {
          key: "completed",
          label: "Show Completed",
          value: this.showFinisedPolls,
        },
      ];
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
  },
  methods: {
    alertClose() {
      this.alertMessage = "";
      this.alertType = "";
    },
    updateOpen(value) {
      this.openUsersList = value;
    },
    formatDate(dateTimeStr) {
      if (!dateTimeStr) return "";
      const utcDateTime = DateTime.fromISO(dateTimeStr, { zone: "utc" });
      const localDateTime = utcDateTime.setZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
      return localDateTime.toFormat("dd MMMM, yyyy");
    },
    formatStatus(status) {
      return status
        .toLowerCase()
        .split("_")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" ");
    },
    getStatusClasses(status) {
      switch (status) {
        case "DRAFT":
          return "border-amber-100 bg-amber-50 text-amber-600";
        case "SCHEDULED":
          return "border-sky-100 bg-sky-50 text-sky-600";
        case "COMPLETED":
          return "border-emerald-100 bg-emerald-50 text-emerald-600";
        case "ACTIVE":
          return "border-primary/30 bg-primary/10 text-primary";
        default:
          return "border-gray-200 bg-gray-100 text-gray-600";
      }
    },
    handleCheckboxChange({ key, value }) {
      if (key === "active") {
        this.showActivePolls = value;
      } else if (key === "completed") {
        this.showFinisedPolls = value;
      }
    },
    handleDateChange({ type, value }) {
      if (type === "start") {
        this.showPollStartTime = value;
        this.pollDateStart = value || null;
        this.currentPage = 1;
        this.getPolls(this.pollDateStart, this.pollDateEnd);
      } else if (type === "end") {
        this.showPollEndTime = value;
        this.pollDateEnd = value || null;
        this.currentPage = 1;
        this.getPolls(this.pollDateStart, this.pollDateEnd);
      }
    },
    handlePageSizeChange(newSize) {
      this.pageSize = newSize;
      this.getPolls(this.pollDateStart, this.pollDateEnd);
    },
    viewPoll(poll) {
      this.$router.push({
        name: "poll-result",
        params: { pollId: poll.id },
      });
    },
    async getPolls(startDate = null, endDate = null) {
      this.loading = true;
      const startTime = Date.now();
      try {
        const backendSortField = this.sortField
          ? this.sortFieldMapping[this.sortField]
          : null;

        const response = await getAllQuestionnaires(
          this.showActivePolls ? true : undefined,
          this.pollStatus,
          this.currentPage,
          this.pageSize,
          startDate,
          endDate,
          backendSortField,
          this.sortDirection
        );
        this.polls = response.items;
        this.totalItems = response.total_count;
        this.$emit("update-total", this.totalItems);
      } catch (error) {
        console.error("Failed to get polls:", error);
        this.alertMessage = "Failed to get polls. Please try again later.";
        this.alertType = "dismiss";
      } finally {
        const elapsedTime = Date.now() - startTime;
        const minLoadingTime = 200;
        if (elapsedTime < minLoadingTime) {
          setTimeout(() => {
            this.loading = false;
          }, minLoadingTime - elapsedTime);
        } else {
          this.loading = false;
        }
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getPolls(this.pollDateStart, this.pollDateEnd);
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getPolls(this.pollDateStart, this.pollDateEnd);
      }
    },
    goToPage(page) {
      this.currentPage = page;
      this.getPolls(this.pollDateStart, this.pollDateEnd);
    },
    handleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
      this.currentPage = 1;
      this.getPolls(this.pollDateStart, this.pollDateEnd);
    },
    clearFilters() {
      this.showActivePolls = true;
      this.showFinisedPolls = false;
      this.showPollStartTime = "";
      this.showPollEndTime = "";
      this.pollDateStart = null;
      this.pollDateEnd = null;
      this.pollStatus = null;
      this.currentPage = 1;
      this.getPolls();
    },
  },
};
</script>
